import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useMutation } from '@tanstack/react-query';

import { UserServiceAPIs } from '@zeta-services';
import { IRefreshTokenRequest } from '@zeta-types';
import { clearLocalStorageExcept, enqueueSnackbar, isValidToken, STORAGE_KEY, useUserDetailsStore } from '@zeta-core';

export function IdleTimeoutHandler() {
  const isRegistrationRoute = window.location.pathname.startsWith('/registration');
  // State to control the warning dialog visibility
  const [dialogOpen, setDialogOpen] = useState(false);
  const userDetails = useUserDetailsStore((state) => state.user);
  const accessToken = sessionStorage.getItem(STORAGE_KEY);
  const isTokenValid = accessToken && isValidToken(accessToken);
  const shouldDialogOpen = !!isTokenValid && dialogOpen;
  const dialogOpenRef = useRef(dialogOpen);
  // Refs to store timeout IDs for warning and auto-logout
  const warningTimeoutId = useRef(null);
  const logoutTimeoutId = useRef(null);

  // Duration constants (in milliseconds)
  const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 minutes for warning
  const LOGOUT_LIMIT = 21 * 60 * 1000; // 21 minutes for auto-logout

  const userRefreshToken = useMutation({
    mutationFn: (payload: IRefreshTokenRequest) => UserServiceAPIs().userRefreshToken(payload),

    onSuccess: ({ data: response }: { data: { accessToken: string } }) => {
      if (response) {
        sessionStorage.setItem(STORAGE_KEY, response.accessToken);
        enqueueSnackbar('Session refreshed successfully.', {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      }
    },
    onError: () => {
      enqueueSnackbar('Session refresh failed!', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    },
  });

  // Function to handle the actual logout action
  const handleLogout = () => {
    if (window.location.pathname.startsWith('/registration')) {
      return;
    }

    console.log('Logging out due to inactivity...');
    // Clear any session storage or cookies if applicable
    try {
      sessionStorage.clear();
      clearLocalStorageExcept(); // Preserves zeta_cookie_consent
    } catch (e) {
      /* ignore if storage is not available */
    }
    setDialogOpen(false); // Close the dialog if it's open
    resetTimers(); // Clear any existing timers
    // Redirect to login page (simulate logout)
    enqueueSnackbar('You have been logged out due to inactivity.', {
      variant: 'warning',
      autoHideDuration: 5000,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
    });
    window.location.href = '/auth/login';
  };

  // Function to reset (or start) the timers for inactivity
  const resetTimers = () => {
    // Only reset timers if no warning is currently showing
    if (!shouldDialogOpen && !dialogOpen) {
      if (warningTimeoutId.current) clearTimeout(warningTimeoutId.current);
      if (logoutTimeoutId.current) clearTimeout(logoutTimeoutId.current);
      // Start a new warning timer
      warningTimeoutId.current = setTimeout(() => {
        setDialogOpen(true); // show the "Are you still there?" dialog
      }, INACTIVITY_LIMIT);
      // Start a new logout timer (one minute after warning)
      logoutTimeoutId.current = setTimeout(() => {
        // If the user still hasn't responded, force logout
        handleLogout();
      }, LOGOUT_LIMIT);
    }
  };

  useEffect(() => {
    dialogOpenRef.current = dialogOpen;
  }, [dialogOpen]);

  // Effect to set up event listeners for user activity on mount
  useEffect(() => {
    if (isRegistrationRoute) {
      return;
    }

    // List of events that indicate user is active
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'touchmove', 'wheel'];
    // Event handler for any user activity event
    const handleActivity = () => {
      if (!dialogOpenRef.current) {
        resetTimers();
      }
    };
    // Attach event listeners to window for each activity event
    activityEvents.forEach((evt) => window.addEventListener(evt, handleActivity));
    // Start the initial timers
    resetTimers();

    // Cleanup function to remove listeners and clear timers on unmount
    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleActivity));
      if (warningTimeoutId.current) clearTimeout(warningTimeoutId.current);
      if (logoutTimeoutId.current) clearTimeout(logoutTimeoutId.current);
    };
  }, [isRegistrationRoute]);

  // Handler for the "I am here" button in the dialog
  const handleStayActive = () => {
    setDialogOpen(false);
    // User confirmed they are here, so reset the timers for a fresh start
    resetTimers();
    if (sessionStorage.getItem('refreshToken') && userDetails?.id) {
      userRefreshToken.mutateAsync({
        refreshToken: sessionStorage.getItem('refreshToken') || '',
        userID: userDetails.id,
      });
    }
  };

  return (
    <Dialog
      open={shouldDialogOpen}
      /* Don't allow clicking outside to close the dialog without action: */
      onClose={() => {
        // Prevent closing the dialog by clicking outside
      }}
    >
      <DialogTitle>Session About To Expire</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have been inactive for a while. Click "I am here" to continue your session.
          <br />
          You will be logged out automatically in 1 minute if no action is taken.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleStayActive} color="primary">
          I am here
        </Button>
      </DialogActions>
    </Dialog>
  );
}
